// Plain words for "Aurora's server can't be reached at all", factored out of
// App.tsx so the mapping is unit-testable (same shape as launch-limit-errors).
//
// Until 2026-10-17 a launch that failed because the host was down surfaced
// the browser's raw TypeError text — "Failed to fetch" (Chrome), "Load
// failed" (Safari) — in the red error strip. That day the VPS was
// power-cycled by the provider for ~14 minutes; anyone returning from Stripe
// in that window would have read a fresh charge followed by "Failed to
// fetch", with nothing about whether the money or the code was safe.
//
// What IS true in that moment, and what the copy leans on: the access code
// is emailed by the server's Stripe-webhook handler
// (payment_email.send_payment_received), Stripe retries the webhook for days
// if the server is down when the payment lands, and re-entering a paid code
// never charges (checkout idempotency + reconcile). So the code arrives by
// email whether or not this tab survives, and a retry is free.

// Gateway-class statuses that mean "nothing answered": traefik with the app
// container gone (502/504 — the deploy's maintenance middleware deliberately
// leaves 503 alone because the app's OWN 503s carry a JSON detail the wizard
// renders: preview pool busy, queue full, grant key missing) and Cloudflare's
// 520–529 origin-error range for a dead or unreachable host.
const GATEWAY_STATUSES = new Set([502, 504]);

export function isServerUnreachable(e: unknown): boolean {
  // fetch() rejects with a TypeError for every network-level failure: DNS,
  // refused connection, the browser offline — and a CORS-blocked response.
  // That last one is how a dead origin looks from the Pages SPA in prod: a
  // Cloudflare 52x page carries no Access-Control headers, so the browser
  // hides the status and the call rejects.
  if (e instanceof TypeError) return true;
  const err = e as { status?: unknown; code?: unknown; detail?: unknown } | null;
  const status = err?.status;
  if (typeof status !== "number") return false;
  if (GATEWAY_STATUSES.has(status)) return true;
  if (status >= 520 && status <= 529) return true;
  // A bare 503 (no code, no detail) is traefik with no backend registered;
  // one WITH a detail is the app speaking, and the detail is the message.
  if (status === 503) return !err?.code && !err?.detail;
  return false;
}

export const SERVER_UNREACHABLE_MESSAGE =
  "Aurora's server isn't reachable right now, so your analysis hasn't " +
  "started. Try again in a few minutes.";

// Shown beside the error strip only for a user who paid THIS visit (the
// Stripe-redirect flag), the one reader for whom "server unreachable" is a
// question about their money.
export const PAID_UNREACHABLE_NOTICE =
  "Your payment went through at Stripe. Your access code is on its way to " +
  "your email, and entering it again never charges you twice — if this keeps " +
  "failing, come back later with that code.";
